import { Menu, rem } from "@mantine/core";
import debounce from "lodash/debounce";
import { Suspense, useEffect, useMemo } from "react";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { MaterialSelect } from "#modules/quoteRequestLines/MaterialSelect";
import { useBulkUpdateMaterial } from "#modules/quoteRequestLines/useBulkUpdateMaterial";
import { useBulkUpdateQuoteRequestLineQuantity } from "#modules/quoteRequestLines/view/useBulkUpdateQuoteRequestLineQuantity";
import { useDeleteManyQuoteRequestLine } from "#modules/quoteRequestLines/view/useDeleteManyQuoteRequestLine";
import { SurfaceTreatmentSelect } from "#modules/quoteRequestLines/views/PredictionData/SurfaceTreatmentSelect";
import { SortSection } from "#modules/quoteRequests/view/SortSection";
import { TransportSelect } from "#modules/quoteRequests/view/TransportSelect";
import { SortOrder, SortProperty } from "#modules/quoteRequests/view/useQuoteRequestLinesSorting";
import { Icon } from "#shared/icons/Icon";
import { ROUTES } from "#shared/routes";
import { useAnalytics, AnalyticsEvent } from "#shared/useAnalytics";
import { ActionIcon } from "#shared/view/components/ActionIcon";
import { Box } from "#shared/view/components/Box";
import { Group } from "#shared/view/components/Group";
import { Stack } from "#shared/view/components/Stack";
import { Checkbox } from "#shared/view/designSystem/Checkbox";
import { NumberInput } from "#shared/view/designSystem/inputs/NumberInput";
import { Skeleton } from "#shared/view/designSystem/Skeleton";
import { Tooltip } from "#shared/view/designSystem/Tooltip";
import { isPartBeingAnalyzed, MaterialGradeType } from "shared";

import { useAssembliesContext } from "../context/AssembliesContext";
import { BulkPartTypeSelect } from "./BulkPartTypeSelect";
import { BulkValidateMenuItem } from "./BulkValidateMenuItem";

type Props = {
  quoteRequestId: string;
  orderQuoteRequestLines: (property: SortProperty, order?: SortOrder) => void;
  hasBatches: boolean;
};

type FormValues = {
  layout: {
    materialLayout: {
      name: string;
      materialGrade: MaterialGradeType | null;
    } | null;
  };
};

export const MultiActionMenu = ({ quoteRequestId, orderQuoteRequestLines, hasBatches }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { assemblies, selectedQuoteRequestLines, setSelectedQuoteRequestLines } =
    useAssembliesContext();

  const areAllParentSelected = useMemo(() => {
    return assemblies.every((a) =>
      selectedQuoteRequestLines.some((qrl) => qrl.id === a.quoteRequestLine.id),
    );
  }, [assemblies, selectedQuoteRequestLines]);
  const areSomeSelected = selectedQuoteRequestLines.length > 0;
  const isAnySelectedPartAnalyzing = useMemo(() => {
    const selectedIds = new Set(selectedQuoteRequestLines.map((qrl) => qrl.id));
    return assemblies.some(
      (a) =>
        (selectedIds.has(a.quoteRequestLine.id) && isPartBeingAnalyzed(a.quoteRequestLine.part)) ||
        a.children.some(
          (c) =>
            selectedIds.has(c.quoteRequestLine.id) && isPartBeingAnalyzed(c.quoteRequestLine.part),
        ),
    );
  }, [assemblies, selectedQuoteRequestLines]);
  const hasSomeChildrenSelected = useMemo(
    () =>
      assemblies.some((a) =>
        a.children.some((c) =>
          selectedQuoteRequestLines.some((qrl) => qrl.id === c.quoteRequestLine.id),
        ),
      ),
    [assemblies, selectedQuoteRequestLines],
  );

  const [isMaterialLoading, setIsMaterialLoading] = useState(false);
  const [quantity, setQuantity] = useState<number | undefined>(undefined);
  const analytics = useAnalytics();
  const { bulkUpdateMaterialAsync } = useBulkUpdateMaterial();
  const { bulkUpdateQuoteRequestLineQuantity } = useBulkUpdateQuoteRequestLineQuantity();
  const { deleteManyQuoteRequestLine } = useDeleteManyQuoteRequestLine({
    onSuccess: () => {
      setSelectedQuoteRequestLines([]);
      if (areAllParentSelected) {
        navigate(ROUTES.partner);
      }
    },
  });
  const formMethods = useForm<FormValues>();

  const onMaterialChange = async (
    materialName: string,
    materialGrade: MaterialGradeType | null,
  ) => {
    if (!materialName && !materialGrade) {
      return;
    }

    analytics.capture(AnalyticsEvent.BulkMaterialChanged, {
      selected_count: selectedQuoteRequestLines.length,
      material_name: materialName,
      material_grade: materialGrade,
    });

    formMethods.reset({
      layout: {
        materialLayout: { name: "" },
      },
    });

    setIsMaterialLoading(true);

    await bulkUpdateMaterialAsync({
      ids: selectedQuoteRequestLines.map((qrl) => qrl.id),
      materialName,
      materialGrade,
    });

    setIsMaterialLoading(false);
  };

  const updateQuantityDebounced = useMemo(
    () =>
      debounce((newQuantity: number) => {
        bulkUpdateQuoteRequestLineQuantity({
          ids: selectedQuoteRequestLines.map((qrl) => qrl.id),
          quantity: newQuantity,
        });
      }, 1000),
    [selectedQuoteRequestLines, bulkUpdateQuoteRequestLineQuantity],
  );

  const onQuantityChange = (newQuantity: number) => {
    if (isNaN(newQuantity) || newQuantity <= 0) {
      return;
    }

    analytics.capture("batch_action:quantity_changed", {
      selected_count: selectedQuoteRequestLines.length,
      quantity: newQuantity,
    });
    setQuantity(newQuantity);
    updateQuantityDebounced(newQuantity);
  };

  const handleBulkDelete = () => {
    deleteManyQuoteRequestLine(selectedQuoteRequestLines.map((qrl) => qrl.id));
  };

  const selectAllParentQuoteRequestLines = () => {
    setSelectedQuoteRequestLines(assemblies.map((assembly) => assembly.quoteRequestLine));
  };

  const selectNoneQuoteRequestLines = () => {
    setSelectedQuoteRequestLines([]);
  };

  useEffect(() => {
    setQuantity(undefined);
  }, [selectedQuoteRequestLines]);

  const isQuantityDisabled =
    !areSomeSelected || hasSomeChildrenSelected || isAnySelectedPartAnalyzing;
  const quantityDisabledTooltip = isAnySelectedPartAnalyzing
    ? t("quoteRequestLine.bulkActionAnalyzingTooltip")
    : hasSomeChildrenSelected
      ? t("quoteRequestLine.bulkQuantityAssemblyChildrenTooltip")
      : undefined;
  const materialActionDisabledTooltip = isAnySelectedPartAnalyzing
    ? t("quoteRequestLine.bulkActionAnalyzingTooltip")
    : undefined;

  return (
    <Group gap="xs" justify="space-between" align="center">
      <Group gap="xs">
        <Stack align="center" gap="xs">
          <Checkbox
            size="xs"
            onChange={
              areAllParentSelected ? selectNoneQuoteRequestLines : selectAllParentQuoteRequestLines
            }
            checked={areAllParentSelected}
            styles={{
              input: { cursor: "pointer" },
            }}
          />
        </Stack>

        <FormProvider {...formMethods}>
          <Tooltip
            label={materialActionDisabledTooltip}
            disabled={!materialActionDisabledTooltip}
            multiline
            w={300}
          >
            <Box
              component="span"
              display="inline-block"
              style={{ cursor: isAnySelectedPartAnalyzing ? "not-allowed" : undefined }}
            >
              <Suspense fallback={<Skeleton width={200} height={32} />}>
                <MaterialSelect
                  disabled={!areSomeSelected || isMaterialLoading || isAnySelectedPartAnalyzing}
                  onChange={onMaterialChange}
                  size="sm"
                  miw={200}
                  withLabel={false}
                />
              </Suspense>
            </Box>
          </Tooltip>
          <Tooltip
            label={materialActionDisabledTooltip}
            disabled={!materialActionDisabledTooltip}
            multiline
            w={300}
          >
            <Box
              component="span"
              display="inline-block"
              style={{ cursor: isAnySelectedPartAnalyzing ? "not-allowed" : undefined }}
            >
              <SurfaceTreatmentSelect
                quoteRequestLineIds={selectedQuoteRequestLines.map((qrl) => qrl.id)}
                size="sm"
                disabled={!areSomeSelected || isAnySelectedPartAnalyzing}
                placeholder={t("qrl.surfaceTreatment")}
              />
            </Box>
          </Tooltip>
          <BulkPartTypeSelect
            selectedQuoteRequestLines={selectedQuoteRequestLines}
            assemblies={assemblies}
            disabled={!areSomeSelected || isAnySelectedPartAnalyzing}
            disabledTooltip={materialActionDisabledTooltip}
            size="sm"
          />
        </FormProvider>

        <Tooltip
          label={quantityDisabledTooltip}
          disabled={!quantityDisabledTooltip}
          multiline
          w={300}
        >
          <Box
            component="span"
            display="inline-block"
            style={{ cursor: isQuantityDisabled ? "not-allowed" : undefined }}
          >
            <NumberInput
              placeholder={t("qrl.quantity")}
              min={0}
              size="xs"
              disabled={isQuantityDisabled}
              value={quantity}
              onChange={onQuantityChange}
              styles={{
                input: {
                  width: "auto",
                  minWidth: 100,
                  fontSize: 10,
                  height: 28,
                  minHeight: 28,
                },
              }}
            />
          </Box>
        </Tooltip>

        <TransportSelect quoteRequestId={quoteRequestId} hasBatches={hasBatches} />

        <Menu shadow="md" width={250} position="bottom-start">
          <Menu.Target>
            <ActionIcon size="lg">
              <Icon name="moreHorizontal" size={24} />
            </ActionIcon>
          </Menu.Target>

          <Menu.Dropdown>
            <BulkValidateMenuItem
              quoteRequestLineIds={selectedQuoteRequestLines.map((qrl) => qrl.id)}
              quoteRequestId={quoteRequestId}
              disabled={!areSomeSelected}
            />
            <Menu.Item
              disabled={!areSomeSelected}
              leftSection={<Icon name="trash" style={{ width: rem(14), height: rem(14) }} />}
              onClick={handleBulkDelete}
            >
              {t("quoteRequestLine.actions.bulkDelete")}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
      <SortSection orderQuoteRequestLines={orderQuoteRequestLines} />
    </Group>
  );
};
